import type { Lens } from '@hookform/lenses';
import { useWatch } from 'react-hook-form';
import { z } from 'zod/v4';
import { USD_CURRENCY_MASK } from '@interxfi/design-system/components/input/text-input/masks';
import type {
  IxcFundingV1BatchFundingControls,
  IxcFundingV1BatchFundingControlsLimits,
  IxcFundingV1BatchFundingControlsLimitsAutoApproveUnder,
  IxcFundingV1BatchFundingControlsLimitsPerBatchMaximums,
} from '@interxfi/types/generated/api';
import { parseNumber } from '@interxfi/utilities/number/parse';
import { AccordionFormGroup } from '../../../../../../components/card-group/form/accordion-form-group';
import { AppSwitchInputWithLens } from '../../../../../../components/form/app-switch-input';
import { AppTextInputWithLens } from '../../../../../../components/form/app-text-input';
import { addDefinedValues } from '../../../../../../util/has-value';
import {
  ALLOW_EMPTY_STRING_OR_EMPTY_USD_VALUE,
  DEFAULT_USD_CURRENCY_VALIDATION_DISPLAY_FORMATTER,
  getNumericStringValidator,
} from '../../../../../../util/validation';

const programControlsBatchFundingLimitsFormSchema = z.object({
  max: getNumericStringValidator('Max Amount', {
    required: ALLOW_EMPTY_STRING_OR_EMPTY_USD_VALUE,
    gte: 0,
    displayFormatter: DEFAULT_USD_CURRENCY_VALIDATION_DISPLAY_FORMATTER,
  }),
  autoApproveUnder: getNumericStringValidator('Auto-Approve Under', {
    required: ALLOW_EMPTY_STRING_OR_EMPTY_USD_VALUE,
    gte: 0,
    displayFormatter: DEFAULT_USD_CURRENCY_VALIDATION_DISPLAY_FORMATTER,
  }),
});

type ProgramControlsBatchFundingLimitsFormValues = z.infer<typeof programControlsBatchFundingLimitsFormSchema>;

const defaultProgramControlsBatchFundingLimitsFormValues: ProgramControlsBatchFundingLimitsFormValues = {
  max: '',
  autoApproveUnder: '',
};

const programControlsBatchFundingSettingFormSchema = z.object({
  canDeposit: z.boolean(),
  canWithdraw: z.boolean(),
});

type ProgramControlsBatchFundingSettingFormValues = z.infer<typeof programControlsBatchFundingSettingFormSchema>;

const defaultProgramControlsBatchFundingSettingFormValues: ProgramControlsBatchFundingSettingFormValues = {
  canDeposit: false,
  canWithdraw: false,
};

export const programControlsBatchFundingFormSchema = z.object({
  ach: programControlsBatchFundingSettingFormSchema,
  sameDayAch: programControlsBatchFundingSettingFormSchema,
  deposit: programControlsBatchFundingLimitsFormSchema,
  withdrawal: programControlsBatchFundingLimitsFormSchema,
});

export type ProgramControlsBatchFundingFormValues = z.infer<typeof programControlsBatchFundingFormSchema>;

export const programControlsBatchFundingFormDefaultValues: ProgramControlsBatchFundingFormValues = {
  ach: defaultProgramControlsBatchFundingSettingFormValues,
  sameDayAch: defaultProgramControlsBatchFundingSettingFormValues,
  deposit: defaultProgramControlsBatchFundingLimitsFormValues,
  withdrawal: defaultProgramControlsBatchFundingLimitsFormValues,
};

export function getInitialFormValuesForProgramBatchFundingControls(
  data: IxcFundingV1BatchFundingControls | undefined,
): ProgramControlsBatchFundingFormValues {
  if (!data) {
    return programControlsBatchFundingFormDefaultValues;
  }

  return {
    ach: {
      canDeposit: Boolean(data.settings?.ach?.canDeposit),
      canWithdraw: Boolean(data.settings?.ach?.canWithdraw),
    },
    sameDayAch: {
      canDeposit: Boolean(data.settings?.sameDayAch?.canDeposit),
      canWithdraw: Boolean(data.settings?.sameDayAch?.canWithdraw),
    },
    deposit: {
      max: data.limits?.perBatchMaximums?.deposit?.toString() || '',
      autoApproveUnder: data.limits?.autoApproveUnder?.deposit?.toString() || '',
    },
    withdrawal: {
      max: data.limits?.perBatchMaximums?.withdrawal?.toString() || '',
      autoApproveUnder: data.limits?.autoApproveUnder?.withdrawal?.toString() || '',
    },
  };
}

export function formValuesToProgramBatchFundingControls(values: ProgramControlsBatchFundingFormValues): IxcFundingV1BatchFundingControls {
  const perBatchMaximums = addDefinedValues<IxcFundingV1BatchFundingControlsLimitsPerBatchMaximums>(
    {},
    {
      deposit: parseNumber(values.deposit.max),
      withdrawal: parseNumber(values.withdrawal.max),
    },
  );

  const autoApproveUnder = addDefinedValues<IxcFundingV1BatchFundingControlsLimitsAutoApproveUnder>(
    {},
    {
      deposit: parseNumber(values.deposit.autoApproveUnder),
      withdrawal: parseNumber(values.withdrawal.autoApproveUnder),
    },
  );

  return {
    settings: {
      ach: {
        canDeposit: values.ach.canDeposit,
        canWithdraw: values.ach.canWithdraw,
      },
      sameDayAch: {
        canDeposit: values.sameDayAch.canDeposit,
        canWithdraw: values.sameDayAch.canWithdraw,
      },
    },
    limits: addDefinedValues<IxcFundingV1BatchFundingControlsLimits>(
      {},
      {
        perBatchMaximums: Object.keys(perBatchMaximums).length > 0 ? perBatchMaximums : undefined,
        autoApproveUnder: Object.keys(autoApproveUnder).length > 0 ? autoApproveUnder : undefined,
      },
    ),
  };
}

interface ProgramControlsBatchFundingFormProps {
  lens: Lens<ProgramControlsBatchFundingFormValues>;
}

export function ProgramControlsBatchFundingForm({ lens }: ProgramControlsBatchFundingFormProps) {
  const hasEnabledDeposit = useWatch({ ...lens.interop(), compute: (values) => values.ach.canDeposit || values.sameDayAch.canDeposit });
  const hasEnabledWithdrawal = useWatch({ ...lens.interop(), compute: (values) => values.ach.canWithdraw || values.sameDayAch.canWithdraw });

  return (
    <>
      <AccordionFormGroup.Section
        toggleInputElement={<AppSwitchInputWithLens align="space-between" label="Batch ACH Pull" lens={lens.focus('ach.canDeposit')} />}
      />
      <AccordionFormGroup.Section
        toggleInputElement={<AppSwitchInputWithLens align="space-between" label="Batch ACH Push" lens={lens.focus('ach.canWithdraw')} />}
      />

      <AccordionFormGroup.Section
        toggleInputElement={
          <AppSwitchInputWithLens align="space-between" label="Batch Same-Day ACH Pull" lens={lens.focus('sameDayAch.canDeposit')} />
        }
      />
      <AccordionFormGroup.Section
        toggleInputElement={
          <AppSwitchInputWithLens align="space-between" label="Batch Same-Day ACH Push" lens={lens.focus('sameDayAch.canWithdraw')} />
        }
      />

      {hasEnabledDeposit || hasEnabledWithdrawal ? (
        <AccordionFormGroup.Section>
          {hasEnabledDeposit ? (
            <AppTextInputWithLens label="Batch Pull Max" lens={lens.focus('deposit.max')} maskOptions={USD_CURRENCY_MASK} />
          ) : null}
          {hasEnabledWithdrawal ? (
            <AppTextInputWithLens label="Batch Push Max" lens={lens.focus('withdrawal.max')} maskOptions={USD_CURRENCY_MASK} />
          ) : null}
          {hasEnabledDeposit ? (
            <AppTextInputWithLens
              label="Auto-Approve Batch Pulls Under"
              lens={lens.focus('deposit.autoApproveUnder')}
              maskOptions={USD_CURRENCY_MASK}
            />
          ) : null}
          {hasEnabledWithdrawal ? (
            <AppTextInputWithLens
              label="Auto-Approve Batch Pushes Under"
              lens={lens.focus('withdrawal.autoApproveUnder')}
              maskOptions={USD_CURRENCY_MASK}
            />
          ) : null}
        </AccordionFormGroup.Section>
      ) : null}
    </>
  );
}
